import {ResourceType} from 'common/report/constants';
import type {CustomEventAlternative, CustomEventDefinition} from 'common/report/util/saved-entities/types';
import {Segmentation} from 'common/types/reports/query';
import {isSegmentationFilter, Segfilter} from 'common/widgets/property-filter-menu/models/segfilter';

export function hasUserFilters(customEvent: CustomEventDefinition, customEvents): boolean {
  if (!customEvent || !customEvent.alternatives || customEvent.is_collect_everything_event) {
    // Guard against empty customEvent reference, regular events and Autotrack events (which never have user filters)
    return false;
  }

  const alternatives = customEvent.alternatives;

  const hasUserFiltersOnThisEvent = alternatives.some((alternative) => {
    return alternative.valid_segfilter?.filters.some((f: Segfilter | Segmentation) =>
      isSegmentationFilter(f) ? f.resourceType === ResourceType.User : f.property.source === `user`,
    );
  });

  if (hasUserFiltersOnThisEvent) {
    return true;
  }

  return alternatives.some((alternative) => {
    const subEventMatch = alternative.event.match(/\$custom_event:(.+)/);
    if (subEventMatch) {
      const subEventId = subEventMatch[1];
      if (!customEvents) {
        return false;
      }
      const subEvent = customEvents.find((event) => subEventId === `${event.id}`);
      return hasUserFilters(subEvent, customEvents);
    }

    return false;
  });
}

function doesAlternativesHaveAutotrackEvent(
  eventToCheck: CustomEventDefinition,
  customEvents: CustomEventDefinition[],
) {
  return eventToCheck.alternatives.find((alternativeEvent: CustomEventAlternative) => {
    const eventName = alternativeEvent.event;
    const isCustomEvent = eventName.startsWith(`$custom_event:`);
    const customEventId = isCustomEvent ? Number(eventName.replace(/^\$custom_event:/, ``)) : null;
    if (!isCustomEvent) {
      return false;
    } else {
      const customEvent = customEvents.find((ev: CustomEventDefinition) => ev.id === customEventId);
      // if it can't find the event as autotrack or custom event, might be hidden. ignore for now.
      if (!customEvent) {
        return false;
      } else if (customEvent.isCollectEverything || customEvent.is_collect_everything_event) {
        return true;
      } else {
        // keep going until we hit a non custom event or an autotrack event
        return doesAlternativesHaveAutotrackEvent(customEvent, customEvents);
      }
    }
  });
}

export function isAutoTrackCustomEvent(customEvent: CustomEventDefinition, customEvents: CustomEventDefinition[]) {
  if (customEvent.isCollectEverything || customEvent.is_collect_everything_event) {
    return true;
  } else {
    // try to find an alernative event that is an autotrack event, disclude it if we find one
    return doesAlternativesHaveAutotrackEvent(customEvent, customEvents);
  }
}
